import {Button, FormControl, FormField, IconButton} from 'platform/components';
import {Box, HStack, Show, Space, VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery, useGetServiceOrderJobQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useOnMount} from 'shared/web';

import {getRatioFromAmount} from '../../../utils/getRatioFromAmount';
import {EditLabourItemFormType} from '../types/editLabourItemFormSchema';

const DEFAULT_RATIO = 100;

interface EditLabourItemMechanicsProps extends TestIdProps {
  control: FormControl<EditLabourItemFormType>;
  formApi: UseFormReturn<EditLabourItemFormType>;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  isDisabled?: boolean;
}

export function EditLabourItemMechanics(props: EditLabourItemMechanicsProps) {
  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {skip: isNilOrEmpty(props.serviceCaseId)}
  );
  const {data: serviceJob, isLoading: isServiceJobLoading} = useGetServiceOrderJobQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      serviceJobId: props.serviceJobId,
    },
    {
      skip:
        isNilOrEmpty(props.serviceCaseId) ||
        isNilOrEmpty(props.serviceOrderId) ||
        isNilOrEmpty(props.serviceJobId),
    }
  );
  const competencyId = serviceJob?.competencyId;
  const {mechanicOptions, areMechanicsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
      isActive: true,
      withToOffer: true,
      employeeCompetenceId: isNotNilOrEmpty(competencyId) ? competencyId : undefined,
    },
    {
      selectFromResult: ({data, isLoading}) => ({
        mechanicOptions: data?.map((mechanic) => ({
          label: mechanic?.name ?? '',
          value: mechanic?.employeeId ?? '',
        })),
        areMechanicsLoading: isLoading,
      }),
      skip: isNil(serviceCase?.authorizationProfileId) || isServiceJobLoading,
    }
  );

  const {fields, append, remove, update} = useFieldArray({
    control: props.control,
    name: 'assignMechanics',
    keyName: 'rowId',
  });
  const assignedMechanics = useWatch({control: props.control, name: 'assignMechanics'}) ?? [];

  // When a single mechanic remains, that mechanic holds the full quantity at the default ratio.
  const normalizeSingleMechanic = () => {
    const totalQuantity = props.formApi.getValues('quantity') ?? 0;

    props.formApi.setValue('assignMechanics.0.amount', totalQuantity);
    props.formApi.setValue('assignMechanics.0.ratio', DEFAULT_RATIO);
  };

  useOnMount(() => {
    if (fields.length === 1) {
      normalizeSingleMechanic();
    }
  });

  const handleRemoveMechanic = (index: number) => () => {
    remove(index);

    if (fields.length - 1 === 1) {
      normalizeSingleMechanic();
    }
  };

  const handleMechanicChange = (index: number) => (value: string | number | string[] | null) => {
    if (!isString(value)) {
      return;
    }

    update(index, {
      ...fields[index],
      id: value,
    });
  };

  const handleQuantityChange = (index: number) => (value: number | null) => {
    const totalQuantity = props.formApi.getValues('quantity') ?? 0;
    const newRatio = getRatioFromAmount(value, totalQuantity) ?? 0;

    props.formApi.setValue(`assignMechanics.${index}.amount`, value ?? 0);
    props.formApi.setValue(`assignMechanics.${index}.ratio`, newRatio);
  };

  const handleRatioChange = (index: number) => (value: number | null) => {
    const totalQuantity = props.formApi.getValues('quantity') ?? 0;
    const newAmount = isNotNil(value) ? Math.round(value * totalQuantity) / 100 : 0;

    props.formApi.setValue(`assignMechanics.${index}.ratio`, value ?? 0);
    props.formApi.setValue(`assignMechanics.${index}.amount`, newAmount);
  };

  return (
    <VStack spacing={4} align="flex-start">
      {fields.map((field, index) => (
        <VStack key={field.rowId} spacing={4} width="100%">
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`assignMechanics.${index}.id`}
                type="choice"
                label={index === 0 ? i18n.t('entity.order.labels.mechanic') : undefined}
                options={(mechanicOptions ?? []).filter(
                  (option) =>
                    option.value === assignedMechanics[index]?.id ||
                    assignedMechanics.every((mechanic) => mechanic.id !== option.value)
                )}
                isLoading={isServiceJobLoading || areMechanicsLoading}
                onChange={handleMechanicChange(index)}
                menuInPortal
                data-testid={suffixTestId(`mechanic-[${index}]`, props)}
              />
            </Box>
            <Box flex={1}>
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    name={`assignMechanics.${index}.amount`}
                    type="number"
                    decimalPlaces={2}
                    label={index === 0 ? i18n.t('entity.order.itemQuantity') : undefined}
                    onChange={handleQuantityChange(index)}
                    isDisabled={fields.length === 1}
                    data-testid={suffixTestId(`quantity-[${index}]`, props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    name={`assignMechanics.${index}.ratio`}
                    type="number"
                    decimalPlaces={2}
                    label={index === 0 ? i18n.t('entity.orderRequest.labels.ratio') : undefined}
                    onChange={handleRatioChange(index)}
                    isDisabled={fields.length === 1}
                    data-testid={suffixTestId(`ratio-[${index}]`, props)}
                  />
                </Box>
                <Show when={fields.length > 1}>
                  <VStack>
                    <Space vertical={index === 0 ? 5 : 0} />
                    <IconButton
                      icon="content/clear"
                      onClick={handleRemoveMechanic(index)}
                      priority="tertiary"
                      severity="danger"
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId(`remove-[${index}]`, props)}
                    />
                  </VStack>
                </Show>
              </HStack>
            </Box>
          </HStack>
        </VStack>
      ))}
      <Button
        variant="link"
        title={i18n.t('entity.order.actions.addMechanic')}
        leftIcon="content/add_circle"
        onClick={() => append({id: null, amount: 0, ratio: 0})}
        data-testid={suffixTestId('add', props)}
        isDisabled={props.isDisabled}
      />
    </VStack>
  );
}
